import React from 'react';
import { createLogo, createUPIQR, createSealOrSign } from '../../utils/imageUtils';
import { roundTo2Decimals, calculateGSTAmount } from '../../utils/numberFormat';
import { formatDateToCompanyFormat } from '../../utils/dateUtils';
import { calculateInvoiceDiscount } from './shared/billFormatUtils';

const PAGE_DIMENSIONS = {
  a4: { portrait: { width: 210, height: 297 }, landscape: { width: 297, height: 210 } },
  a5: { portrait: { width: 148, height: 210 }, landscape: { width: 210, height: 148 } },
};

function blur(val, previewMode) {
  if (!previewMode) return val;
  if (typeof val === 'number') return 'XXX';
  if (typeof val === 'string') return val.replace(/[^\s]/g, 'X');
  return 'XXX';
}

function formatINR(num) {
  if (typeof num !== 'number') num = parseInt(num, 10);
  if (isNaN(num)) return '';
  return num.toLocaleString('en-IN');
}

function numberToWordsIndian(num) {
  const a = [ '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen' ];
  const b = [ '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety' ];
  let str = '';
  if (num >= 10000000) {
    str += numberToWordsIndian(Math.floor(num / 10000000)) + ' Crore ';
    num = num % 10000000;
  }
  if (num >= 100000) {
    str += numberToWordsIndian(Math.floor(num / 100000)) + ' Lakh ';
    num = num % 100000;
  }
  if (num >= 1000) {
    str += numberToWordsIndian(Math.floor(num / 1000)) + ' Thousand ';
    num = num % 1000;
  }
  if (num >= 100) {
    str += numberToWordsIndian(Math.floor(num / 100)) + ' Hundred ';
    num = num % 100;
  }
  if (num > 0) {
    if (str !== '') str += 'and ';
    if (num < 20) str += a[num] + ' ';
    else str += b[Math.floor(num / 10)] + (num % 10 ? ' ' + a[num % 10] : '') + ' ';
  }
  return str.replace(/ +/g, ' ').trim();
}

function numToWords(num) {
  if (typeof num !== 'number') num = parseInt(num, 10);
  if (isNaN(num)) return '';
  if (num === 0) return 'Zero Rupees Only';
  return numberToWordsIndian(num) + ' Rupees Only';
}

function SimpleSalesTemplate({
  billData = {},
  companyDetails = {},
  partyDetails = {},
  bankDetails = {},
  pageSize = 'a4',
  orientation = 'portrait',
  previewMode = false,
  showPlaceholders = false,
}) {
  const visibleItems = (billData.items || []).filter(it => {
    const hasDesc = !!(it.description && String(it.description).trim());
    const hasItem = !!(it.item && String(it.item).trim());
    const hasNumbers = (parseFloat(it.qty) || 0) > 0 || (parseFloat(it.rate) || 0) > 0 || (parseFloat(it.amount) || 0) > 0 || (parseFloat(it.total) || 0) > 0;
    return hasDesc || hasItem || hasNumbers;
  });

  const {
    subtotal,
    discount,
    discountedSubtotal,
    discountRatio,
    grossAmount,
  } = calculateInvoiceDiscount(visibleItems, billData);
  const totalSGST = visibleItems.reduce((sum, item) => {
    const amt = parseFloat(item.amount) || 0;
    const effAmt = amt - (amt * discountRatio);
    return sum + calculateGSTAmount(effAmt, item.sgst);
  }, 0) || 0;
  const totalCGST = visibleItems.reduce((sum, item) => {
    const amt = parseFloat(item.amount) || 0;
    const effAmt = amt - (amt * discountRatio);
    return sum + calculateGSTAmount(effAmt, item.cgst);
  }, 0) || 0;
  const totalIGST = visibleItems.reduce((sum, item) => {
    const amt = parseFloat(item.amount) || 0;
    const effAmt = amt - (amt * discountRatio);
    return sum + calculateGSTAmount(effAmt, item.igst || item.gst);
  }, 0) || 0;
  const grandTotal = roundTo2Decimals(discountedSubtotal + totalSGST + totalCGST + totalIGST);

  const pageDims = PAGE_DIMENSIONS[pageSize]?.[orientation] || PAGE_DIMENSIONS.a4.portrait;
  const isA5 = pageSize === 'a5';
  
  const pageStyle = {
    width: '100%',
    maxWidth: `${pageDims.width}mm`,
    minHeight: `${pageDims.height}mm`,
    margin: '0 auto',
    background: 'white',
    boxSizing: 'border-box',
    padding: isA5 ? '6px' : '10px',
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '4px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
  };

  return (
    <div style={pageStyle} className="print:p-0 print:shadow-none print:border-0" id="simple-sales-template">
      {/* Compact Header */}
      <div className="flex justify-between items-start mb-2" style={{ marginBottom: '8px' }}>
        <div style={{ flex: 1 }}>
          <div className="font-bold" style={{ fontSize: isA5 ? '14px' : '18px' }}>
            {companyDetails.gstinType === 'Regular' && 'TAX INVOICE'}
            {companyDetails.gstinType === 'Composition' && 'BILL OF SUPPLY'}
            {companyDetails.gstinType === 'Unregistered' && 'INVOICE'}
          </div>
          <div style={{ fontSize: isA5 ? '9px' : '11px', color: '#666' }}>
            Inv No: {previewMode ? 'XXX' : billData.invoiceNumber} | 
            Date: {previewMode ? 'XXX' : formatDateToCompanyFormat(billData.date, companyDetails)}
          </div>
        </div>
        <div className="flex items-center">
          {createLogo(companyDetails.logoUrl, companyDetails.firmName || companyDetails.name || 'Company', isA5 ? 'h-10 w-10' : 'h-12 w-12')}
        </div>
      </div>

      {/* Compact Company & Party Info */}
      <div className="flex gap-2 mb-2 simple-party-row" style={{ fontSize: isA5 ? '9px' : '10px' }}>
        <div style={{ flex: 1, padding: '6px', border: '1px solid #ddd', borderRadius: '3px' }}>
          <div className="font-bold" style={{ marginBottom: '3px' }}>From:</div>
          <div className="font-semibold">{companyDetails.name || companyDetails.firmName || '-'}</div>
          <div>{companyDetails.address || '-'}</div>
          <div>GST: {companyDetails.gstin || companyDetails.gstNumber || '-'}</div>
          <div>Mob: {companyDetails.contact || companyDetails.contactNumber || '-'}</div>
        </div>
        <div style={{ flex: 1, padding: '6px', border: '1px solid #ddd', borderRadius: '3px' }}>
          <div className="font-bold" style={{ marginBottom: '3px' }}>To:</div>
          <div className="font-semibold">{previewMode ? 'XXX Name' : partyDetails.name}</div>
          <div>{previewMode ? 'XXX Address' : partyDetails.address}</div>
          <div>GST: {previewMode ? 'XXX' : (partyDetails.gstin || partyDetails.gstNumber || '-')}</div>
          <div>Mob: {previewMode ? 'XXX' : (partyDetails.contact || '-')}</div>
        </div>
      </div>

      {/* Compact Item Table */}
      <div className="overflow-x-auto" style={{ marginBottom: '6px' }}>
        <table className="w-full border" style={{ fontSize: isA5 ? '9px' : '10px' }}>
          <thead>
            <tr className="bg-gray-100">
              <th className="border px-1 py-1" style={{ padding: '4px' }}>#</th>
              <th className="border px-1 py-1" style={{ padding: '4px' }}>Description</th>
              {companyDetails.gstinType === 'Regular' && <th className="border px-1 py-1" style={{ padding: '4px' }}>HSN</th>}
              <th className="border px-1 py-1" style={{ padding: '4px' }}>Qty</th>
              <th className="border px-1 py-1" style={{ padding: '4px' }}>Rate</th>
              {companyDetails.gstinType === 'Regular' && <th className="border px-1 py-1" style={{ padding: '4px' }}>GST%</th>}
              <th className="border px-1 py-1" style={{ padding: '4px' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.map((item, idx) => (
              <tr key={idx}>
                <td className="border px-1 py-1 text-center" style={{ padding: '4px' }}>{idx + 1}</td>
                <td className="border px-1 py-1" style={{ padding: '4px' }}>
                  {item.description}
                  {item.qtyDisplay && (
                    <div style={{ fontSize: '0.85em', color: '#666' }}>{item.qtyDisplay}</div>
                  )}
                </td>
                {companyDetails.gstinType === 'Regular' && (
                  <td className="border px-1 py-1 text-center" style={{ padding: '4px' }}>{item.hsn}</td>
                )}
                <td className="border px-1 py-1 text-center" style={{ padding: '4px' }}>
                  {item.qty} {item.unit || item.quantityMeasurement || 'Nos'}
                </td>
                <td className="border px-1 py-1 text-right" style={{ padding: '4px' }}>{item.rate}</td>
                {companyDetails.gstinType === 'Regular' && (
                  <td className="border px-1 py-1 text-center" style={{ padding: '4px' }}>
                    {item.gst || (parseFloat(item.sgst || 0) + parseFloat(item.cgst || 0) + parseFloat(item.igst || 0))}
                  </td>
                )}
                <td className="border px-1 py-1 text-right" style={{ padding: '4px' }}>
                  {item.total !== undefined && item.total !== null && item.total !== "" ? item.total : item.amount}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Compact Totals Section */}
      <div className="flex gap-2 mb-2 simple-totals-row">
        {/* Payment Details */}
        <div style={{ flex: 1, padding: '6px', fontSize: isA5 ? '9px' : '10px' }}>
          {companyDetails.bankName && <div className="font-semibold mb-1">Payment:</div>}
          {companyDetails.bankName && <div>{companyDetails.bankName}</div>}
          {companyDetails.bankAccount && <div>A/C: {companyDetails.bankAccount}</div>}
          {companyDetails.bankIfsc && <div>IFSC: {companyDetails.bankIfsc}</div>}
          {companyDetails.upiId && <div>UPI: {companyDetails.upiId}</div>}
          {companyDetails.upiQrUrl && (
            <div style={{ marginTop: '4px' }}>{createUPIQR(companyDetails.upiQrUrl, 'w-16 h-16')}</div>
          )}
        </div>

        {/* Summary */}
        <div style={{ flex: 1, padding: '6px', fontSize: isA5 ? '9px' : '10px' }}>
          <div className="font-semibold mb-1">Summary:</div>
          {companyDetails.gstinType === 'Regular' ? (
            <>
              <div>Subtotal: {previewMode ? blur(subtotal, true) : subtotal}</div>
              {discount > 0 && <div>Discount: -{previewMode ? blur(discount, true) : discount}</div>}
              <div>SGST: {previewMode ? blur(totalSGST, true) : totalSGST}</div>
              <div>CGST: {previewMode ? blur(totalCGST, true) : totalCGST}</div>
              <div>IGST: {previewMode ? blur(totalIGST, true) : totalIGST}</div>
              <div className="font-bold border-t pt-1 mt-1">Total: {previewMode ? blur(grandTotal, true) : grandTotal}</div>
            </>
          ) : (
            <>
              <div>Amount: {previewMode ? blur(grossAmount, true) : grossAmount}</div>
              {discount > 0 && <div>Discount: -{previewMode ? blur(discount, true) : discount}</div>}
              <div className="font-bold border-t pt-1 mt-1">Total: {previewMode ? blur(discountedSubtotal, true) : discountedSubtotal}</div>
            </>
          )}
          <div style={{ fontSize: '0.9em', marginTop: '3px' }}>
            {numToWords(roundTo2Decimals(grandTotal))}
          </div>
        </div>
      </div>

      {String(billData.notes || '').trim() && (
        <div style={{ fontSize: isA5 ? '8px' : '9px', padding: '4px', marginBottom: '20px', background: '#eff6ff', borderRadius: '2px' }}>
          <span className="font-semibold">Note: </span>
          <span style={{ whiteSpace: 'pre-line' }}>{previewMode ? blur(String(billData.notes).trim(), true) : String(billData.notes).trim()}</span>
        </div>
      )}

      {/* Terms */}
      {(companyDetails.salesTerms || companyDetails.terms) && (
        <div style={{ fontSize: isA5 ? '8px' : '9px', padding: '4px', marginBottom: '4px', background: '#f9f9f9', borderRadius: '2px' }}>
          <span className="font-semibold">Terms: </span>
          <span>{(companyDetails.salesTerms || companyDetails.terms).split('\n').join(' | ')}</span>
        </div>
      )}

      {companyDetails.gstinType === 'Composition' && (
        <div style={{ fontSize: isA5 ? '8px' : '9px', color: '#dc2626', fontWeight: 'bold', marginBottom: '4px' }}>
          Composition taxable person - Not eligible to collect tax
        </div>
      )}

      {/* Signatures */}
      <div className="flex justify-between simple-sign-row" style={{ marginTop: '24px', paddingTop: '28px', minHeight: '72px', fontSize: isA5 ? '8px' : '9px' }}>
        <div className="text-center">
          <div style={{ borderTop: '1px solid #333', width: '80px', margin: '0 auto 4px' }}></div>
          Receiver's Sign
        </div>
        <div className="text-center">
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', marginBottom: '4px' }}>
            {bankDetails.sealUrl && createSealOrSign(bankDetails.sealUrl, 'Seal', isA5 ? 'h-6 w-6' : 'h-8 w-8')}
            {bankDetails.signUrl && createSealOrSign(bankDetails.signUrl, 'Sign', isA5 ? 'h-6 w-12' : 'h-8 w-16')}
          </div>
          Company Sign & Seal
        </div>
      </div>

      <style>{`
        @media screen and (max-width: 640px) {
          .simple-party-row,
          .simple-totals-row,
          .simple-sign-row {
            flex-direction: column !important;
            gap: 8px !important;
          }
        }

        @media print {
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          @page {
            size: ${pageDims.width}mm ${pageDims.height}mm ${orientation};
            margin: 4mm;
          }
          
          .print\\:p-0 { padding: 0 !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:border-0 { border: none !important; }
          
          img {
            display: block !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            visibility: visible !important;
          }
        }
      `}</style>
    </div>
  );
}

export default SimpleSalesTemplate;
